/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: FaxSendingPage.tsx
 🗓️ Created: 2025-12-03
 🧩 Description:
    Page for sending faxes from the web interface.
    Supports Domain Contextual Filtering pattern.

 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added Domain Filtering)
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback } from 'react';
import { Send, Printer, Upload, X, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useContextStore } from '../../store/context';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { FormBox } from '../../components/ui/FormBox';
import SelectFaxDevice from '../../components/ui/SelectFaxDevice';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { ModuleFormHeader } from '../../components/layouts';
import { fetchOutboundFaxDevices, sendFax } from './api/faxSendingApi';
import FaxProgressModal from './components/FaxProgressModal';
import type { FaxDevice, SendFaxFormState } from './types';

export default function FaxSendingPage() {
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();
  const { selectedDomain } = useContextStore();

  const [devices, setDevices] = useState<FaxDevice[]>([]);
  const [isLoadingDevices, setIsLoadingDevices] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [formData, setFormData] = useState<SendFaxFormState>({
    faxDeviceId: null,
    destinationNumber: '',
    file: null
  });
  const [dragActive, setDragActive] = useState(false);
  const [sendingStatus, setSendingStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [progressModal, setProgressModal] = useState<{
    isOpen: boolean;
    jobId: string | null;
    callUuid: string | null;
    destinationNumber: string;
    fileName: string;
  }>({ isOpen: false, jobId: null, callUuid: null, destinationNumber: '', fileName: '' });

  // Effective domain ID for contextual filtering
  const effectiveDomainId = selectedDomain?.id;

  // Load available fax devices
  useEffect(() => {
    setIsLoadingDevices(true);
    fetchOutboundFaxDevices(effectiveDomainId)
      .then((data) => {
        setDevices(data);
        // Auto-select first device if available
        if (data.length > 0) {
          setFormData(prev => ({ ...prev, faxDeviceId: data[0].id }));
        }
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .catch((error: any) => {
        showError(error.message || t('faxSending:messages.loadDevicesError'));
      })
      .finally(() => setIsLoadingDevices(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveDomainId]);

  // Handle field changes
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChange = useCallback((field: keyof SendFaxFormState, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Handle file selection
  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'image/tiff', 'image/tif'];
      if (!validTypes.includes(file.type) && !file.name.match(/\.(pdf|tiff?)$/i)) {
        showError(t('faxSending:validation.invalidFileType'));
        return;
      }

      // Validate file size (10 MB max)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        showError(t('faxSending:validation.fileTooLarge'));
        return;
      }

      handleChange('file', file);
    }
  }, [handleChange, showError, t]);

  // Handle drag and drop
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'image/tiff', 'image/tif'];
      if (!validTypes.includes(file.type) && !file.name.match(/\.(pdf|tiff?|tif)$/i)) {
        showError(t('faxSending:validation.invalidFileType'));
        return;
      }

      // Validate file size
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        showError(t('faxSending:validation.fileTooLarge'));
        return;
      }

      handleChange('file', file);
    }
  }, [handleChange, showError, t]);

  // Remove file
  const handleRemoveFile = useCallback(() => {
    handleChange('file', null);
  }, [handleChange]);

  // Handle send fax
  const handleSend = useCallback(async () => {
    // Validation
    if (!formData.faxDeviceId) {
      showError(t('faxSending:validation.deviceRequired'));
      return;
    }

    if (!formData.destinationNumber.trim()) {
      showError(t('faxSending:validation.destinationRequired'));
      return;
    }

    if (!formData.file) {
      showError(t('faxSending:validation.fileRequired'));
      return;
    }

    setIsSending(true);
    setSendingStatus('sending');

    try {
      const result = await sendFax(
        formData.faxDeviceId,
        formData.destinationNumber,
        formData.file
      );

      if (result.success) {
        setSendingStatus('success');
        // Open progress modal — polling will track real status
        setProgressModal({
          isOpen: true,
          jobId: result.faxJobId || null,
          callUuid: result.callUuid || null,
          destinationNumber: formData.destinationNumber,
          fileName: formData.file?.name || '',
        });
        // Reset form fields immediately so user can prepare next fax
        setFormData(prev => ({
          ...prev,
          destinationNumber: '',
          file: null,
        }));
        setSendingStatus('idle');
      } else {
        setSendingStatus('error');
        showError(result.message || t('faxSending:messages.sendError'));
      }
    } catch (error) {
      setSendingStatus('error');
      showError(error.message || t('faxSending:messages.sendError'));
    } finally {
      setIsSending(false);
    }
  }, [formData, showError, t]);

  const handleProgressModalClose = useCallback(() => {
    setProgressModal(prev => ({ ...prev, isOpen: false }));
  }, []);

  // Build fax device options for SelectFaxDevice
  const faxDeviceOptions = devices.map((d) => ({
    id: d.id,
    name: d.name,
    number: d.number,
    type: d.type,
  }));

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  return (
    <div className="p-6 pb-20">
      <ModuleFormHeader
        title={t('faxSending:title')}
        icon={<Send className="h-5 w-5" />}
        backButtonText={t('common:back')}
        onBack={() => window.history.back()}
      />

      <div className="mt-8 max-w-4xl mx-auto space-y-4">
        {/* Main Form */}
        <FormBox
          title={t('faxSending:sections.sendFax')}
          icon={<Printer className="h-5 w-5" />}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
            {/* Row 1: Fax Device | Select | Destination Number | Input */}
            <SimpleTooltip
              content={t('faxSending:hints.faxDevice')}
              title={t('faxSending:fields.faxDevice')}
              position="top"
              maxWidth="400px"
            >
              <Label htmlFor="faxDevice" required className="text-left">
                {t('faxSending:fields.faxDevice')}
              </Label>
            </SimpleTooltip>
            <SelectFaxDevice
              value={formData.faxDeviceId}
              onChange={(v) => handleChange('faxDeviceId', v ? String(v) : null)}
              faxDevices={faxDeviceOptions}
              placeholder={t('faxSending:placeholders.selectDevice')}
              disabled={isLoadingDevices || isSending}
            />
            <SimpleTooltip
              content={t('faxSending:hints.destination')}
              title={t('faxSending:fields.destination')}
              position="top"
              maxWidth="400px"
            >
              <Label htmlFor="destination" required className="text-left">
                {t('faxSending:fields.destination')}
              </Label>
            </SimpleTooltip>
            <Input
              id="destination"
              type="tel"
              value={formData.destinationNumber}
              onChange={(e) => handleChange('destinationNumber', e.target.value)}
              placeholder={t('faxSending:placeholders.destination')}
              disabled={isSending}
            />
          </div>
        </FormBox>

        {/* File Upload */}
        <FormBox
          title={t('faxSending:sections.document')}
          icon={<Upload className="h-5 w-5" />}
          variant="elevated"
        >
          <div className="space-y-4">
            {!formData.file ? (
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragActive
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                  } ${isSending ? 'pointer-events-none opacity-50' : ''}`}
              >
                <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-lg font-medium text-foreground mb-2">
                  {t('faxSending:upload.title')}
                </p>
                <p className="text-sm text-muted-foreground mb-4">
                  {t('faxSending:upload.subtitle')}
                </p>
                <label className="inline-block">
                  <input
                    type="file"
                    accept=".pdf,.tif,.tiff,application/pdf,image/tiff"
                    onChange={handleFileSelect}
                    disabled={isSending}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isSending}
                    onClick={() => document.querySelector('input[type="file"]')?.dispatchEvent(new MouseEvent('click'))}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {t('faxSending:upload.browse')}
                  </Button>
                </label>
                <p className="text-xs text-muted-foreground mt-4">
                  {t('faxSending:upload.supportedFormats')}
                </p>
              </div>
            ) : (
              <div className="border border-border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-primary/10 p-3 rounded-lg">
                      <Printer className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{formData.file.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {formatFileSize(formData.file.size)}
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleRemoveFile}
                    disabled={isSending}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </FormBox>

        {/* Send Button and Status */}
        <div className="flex items-center justify-between">
          <div className="flex-1">
            {sendingStatus === 'sending' && (
              <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="font-medium">{t('faxSending:status.sending')}</span>
              </div>
            )}
            {sendingStatus === 'success' && (
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium">{t('faxSending:status.success')}</span>
              </div>
            )}
            {sendingStatus === 'error' && (
              <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                <AlertCircle className="h-5 w-5" />
                <span className="font-medium">{t('faxSending:status.error')}</span>
              </div>
            )}
          </div>

          <Button
            onClick={handleSend}
            disabled={!formData.faxDeviceId || !formData.destinationNumber || !formData.file || isSending}
            className="flex items-center gap-2"
          >
            {isSending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('faxSending:actions.sending')}
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                {t('faxSending:actions.send')}
              </>
            )}
          </Button>
        </div>

        {/* Information Box */}
        <div className="bg-muted/30 border border-border rounded-lg p-4">
          <h4 className="font-medium text-foreground mb-2">{t('faxSending:info.title')}</h4>
          <ul className="space-y-1 text-sm text-muted-foreground">
            <li>• {t('faxSending:info.formats')}</li>
            <li>• {t('faxSending:info.maxSize')}</li>
            <li>• {t('faxSending:info.notification')}</li>
            <li>• {t('faxSending:info.t38')}</li>
          </ul>
        </div>
      </div>

      {/* Fax Progress Modal */}
      <FaxProgressModal
        isOpen={progressModal.isOpen}
        jobId={progressModal.jobId}
        callUuid={progressModal.callUuid}
        destinationNumber={progressModal.destinationNumber}
        fileName={progressModal.fileName}
        onClose={handleProgressModalClose}
      />
    </div>
  );
}

